/**
 * Geo-map Autonomics robot sprite.
 *
 * The pin and icon-rotate stay on the pose. icon-offset moves the PNG center
 * so the pose sits at origin_to_front from the front edge of the canvas.
 * Mapbox multiplies that offset by icon-size and rotates it with the icon.
 *
 * Sweeper, Combo and Omni share one pixel scale: the full 81px canvas height is
 * Combo/Omni body length (2.13 m). Sweeper is shorter; empty padding is at the
 * rear of the same canvas. Do not scale each type by its own length_m — that
 * makes Sweeper denser and puts rear_axle too far back into the padding.
 *
 * Sprites are 42×81 and point north (nose up) at yaw 0. +y in icon-offset is down.
 */

export const ROBOT_MAP_ICON_HEIGHT_PX = 81
/** Ground length of the full Autonomics map PNG (Combo / Omni). */
export const ROBOT_MAP_SPRITE_LENGTH_M = 2.13

export const ROBOT_ICON_ZOOM_COARSE = 15
/** Zoom where the 81px canvas first equals ROBOT_MAP_SPRITE_LENGTH_M. */
export const ROBOT_ICON_ZOOM_FINE = 22
/** One step past that. icon-size doubles so the canvas stays 2.13 m. */
export const ROBOT_ICON_ZOOM_MAX = 23
/** Marker size at city zoom, before the body is drawn in meters. */
export const ROBOT_ICON_SIZE_COARSE = 0.6
/** Fallback at fine zoom when the robot type has no chassis geometry. */
export const ROBOT_ICON_SIZE_FINE_FALLBACK = 3.2

const WEB_MERCATOR_CIRCUMFERENCE_M = 40075016.68557849
const MAPBOX_TILE_PX = 512

export function metersPerPixelAtZoom(zoom: number, latitudeDeg: number): number {
  const cosLat = Math.cos((latitudeDeg * Math.PI) / 180)
  return (WEB_MERCATOR_CIRCUMFERENCE_M * cosLat) / (MAPBOX_TILE_PX * 2 ** zoom)
}

/** How far the physical body-length midpoint sits ahead of the pose. */
export function poseToLengthCenterM(lengthM?: number | null, originToFrontM?: number | null): number | null {
  if (typeof lengthM !== 'number' || typeof originToFrontM !== 'number') return null
  if (!Number.isFinite(lengthM) || !Number.isFinite(originToFrontM) || lengthM <= 0) return null
  return originToFrontM - lengthM / 2
}

/**
 * How far the PNG canvas center sits ahead of the pose under the shared sprite scale.
 * Same for Sweeper and Combo when origin_to_front matches (empty rear does not move the nose).
 */
export function poseToSpriteCenterM(originToFrontM?: number | null): number | null {
  if (typeof originToFrontM !== 'number' || !Number.isFinite(originToFrontM)) return null
  return originToFrontM - ROBOT_MAP_SPRITE_LENGTH_M / 2
}

export function hasRobotMapChassisGeometry(
  lengthM?: number | null,
  originToFrontM?: number | null
): boolean {
  return (
    (typeof lengthM === 'number' && Number.isFinite(lengthM) && lengthM > 0) ||
    (typeof originToFrontM === 'number' && Number.isFinite(originToFrontM))
  )
}

/** Source-image pixels. Negative y shifts the sprite forward (nose up). */
export function robotIconOffsetPx(originToFrontM?: number | null): [number, number] {
  const ahead = poseToSpriteCenterM(originToFrontM)
  if (ahead == null) return [0, 0]
  return [0, -((ahead / ROBOT_MAP_SPRITE_LENGTH_M) * ROBOT_MAP_ICON_HEIGHT_PX)]
}

/**
 * icon-size at zoom 22 so the 81px canvas equals ROBOT_MAP_SPRITE_LENGTH_M on the ground.
 * Same meters/pixel for Sweeper, Combo and Omni.
 */
export function robotIconSizeAtFineZoom(
  hasChassisGeometry: boolean,
  latitudeDeg?: number | null
): number {
  if (!hasChassisGeometry) return ROBOT_ICON_SIZE_FINE_FALLBACK
  if (typeof latitudeDeg !== 'number' || !Number.isFinite(latitudeDeg)) {
    return ROBOT_ICON_SIZE_FINE_FALLBACK
  }
  const metersPerPixel = metersPerPixelAtZoom(ROBOT_ICON_ZOOM_FINE, latitudeDeg)
  if (!(metersPerPixel > 0)) return ROBOT_ICON_SIZE_FINE_FALLBACK
  return ROBOT_MAP_SPRITE_LENGTH_M / (ROBOT_MAP_ICON_HEIGHT_PX * metersPerPixel)
}

/** Pixels at zoom 15, sprite meters at 22, and the same meters at 23 (size ×2). */
export function robotIconSizeExpression(sizeAtFineZoom: number | unknown[]) {
  const sizeAtMaxZoom = typeof sizeAtFineZoom === 'number' ? sizeAtFineZoom * 2 : ['*', sizeAtFineZoom, 2]
  return [
    'interpolate',
    ['exponential', 2],
    ['zoom'],
    ROBOT_ICON_ZOOM_COARSE,
    ROBOT_ICON_SIZE_COARSE,
    ROBOT_ICON_ZOOM_FINE,
    sizeAtFineZoom,
    ROBOT_ICON_ZOOM_MAX,
    sizeAtMaxZoom,
  ]
}

/** Per-feature scale. Each robot carries iconSizeAt22 from the shared sprite length. */
export const robotIconSizeFromFeature = robotIconSizeExpression([
  'coalesce',
  ['get', 'iconSizeAt22'],
  ROBOT_ICON_SIZE_FINE_FALLBACK,
])

export const robotIconOffsetFromFeature = ['coalesce', ['get', 'iconOffset'], ['literal', [0, 0]]]

export function robotIconFeatureProps(
  lengthM: number | null | undefined,
  originToFrontM: number | null | undefined,
  latitudeDeg: number | null | undefined
) {
  const hasChassis = hasRobotMapChassisGeometry(lengthM, originToFrontM)
  return {
    iconSizeAt22: robotIconSizeAtFineZoom(hasChassis, latitudeDeg),
    iconOffset: robotIconOffsetPx(originToFrontM),
  }
}

/** Round latitude so a moving robot does not rebuild the symbol layout every telemetry tick. */
export function iconLayoutLatitude(latitudeDeg?: number | null): number | null {
  if (typeof latitudeDeg !== 'number' || !Number.isFinite(latitudeDeg)) return null
  return Math.round(latitudeDeg * 100) / 100
}

/** Same exponential base-2 curve as the Mapbox icon-size expression. */
export function robotIconSizeAtZoom(
  zoom: number,
  hasChassisGeometry: boolean,
  latitudeDeg?: number | null
): number {
  const fine = robotIconSizeAtFineZoom(hasChassisGeometry, latitudeDeg)
  if (!(typeof zoom === 'number') || !Number.isFinite(zoom) || zoom <= ROBOT_ICON_ZOOM_COARSE) {
    return ROBOT_ICON_SIZE_COARSE
  }
  if (zoom >= ROBOT_ICON_ZOOM_MAX) return fine * 2
  if (zoom >= ROBOT_ICON_ZOOM_FINE) {
    const t = (2 ** zoom - 2 ** ROBOT_ICON_ZOOM_FINE) / (2 ** ROBOT_ICON_ZOOM_MAX - 2 ** ROBOT_ICON_ZOOM_FINE)
    return fine + t * fine
  }
  const z0 = ROBOT_ICON_ZOOM_COARSE
  const z1 = ROBOT_ICON_ZOOM_FINE
  const t = (2 ** zoom - 2 ** z0) / (2 ** z1 - 2 ** z0)
  return ROBOT_ICON_SIZE_COARSE + t * (fine - ROBOT_ICON_SIZE_COARSE)
}

/**
 * Occupancy marker in the stage's layout pixels (before the 1…4 UI zoom).
 * Canvas height is the shared 2.13 m sprite, not the shorter Sweeper body.
 * anchorFromTop is where the pose sits in the PNG (0 = nose, 1 = bottom).
 */
export function floorSpriteMarkerLayout(params: {
  spriteLengthM?: number | null
  originToFrontM?: number | null
  resolutionM?: number | null
  /** Layout pixels per occupancy cell. */
  cellPx: number
  fallbackHeightPx: number
}): { heightPx: number; anchorFromTop: number } {
  const sprite = params.spriteLengthM
  const resolution = params.resolutionM
  const cellPx = params.cellPx
  if (
    typeof sprite !== 'number' ||
    !(sprite > 0) ||
    typeof resolution !== 'number' ||
    !(resolution > 0) ||
    !(cellPx > 0)
  ) {
    return { heightPx: params.fallbackHeightPx, anchorFromTop: 0.5 }
  }
  const front = params.originToFrontM
  const anchorFromTop =
    typeof front === 'number' && Number.isFinite(front) ? Math.min(1, Math.max(0, front / sprite)) : 0.5
  return { heightPx: (sprite / resolution) * cellPx, anchorFromTop }
}

/**
 * Screen pixels from the pose along heading to the rotation handle.
 * Lands past the front edge of the drawn sprite, not mid-body.
 */
export function rotationHandleDistancePx(params: {
  zoom: number
  latitudeDeg?: number | null
  lengthM?: number | null
  originToFrontM?: number | null
  /** Extra gap beyond the nose. */
  marginPx?: number
}): number {
  const margin = params.marginPx ?? 28
  const hasChassis = hasRobotMapChassisGeometry(params.lengthM, params.originToFrontM)
  const size = robotIconSizeAtZoom(params.zoom, hasChassis, params.latitudeDeg)
  const bodyPx = ROBOT_MAP_ICON_HEIGHT_PX * size
  const frontM = params.originToFrontM
  if (typeof frontM === 'number' && Number.isFinite(frontM)) {
    return (frontM / ROBOT_MAP_SPRITE_LENGTH_M) * bodyPx + margin
  }
  return bodyPx * 0.5 + margin
}
